import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Image as ImageIcon, Video, Layers } from "lucide-react";

// Bump this whenever you ship something worth re-announcing.
const RELEASE_VERSION = "2026-02-content-image-tiktok";
const STORAGE_KEY = `pj_whatsnew_${RELEASE_VERSION}`;

const HIGHLIGHTS = [
  { icon: ImageIcon, label: "AI Image Generation + uploads inside the content editor" },
  { icon: Video, label: "TikTok Script & Instagram Caption generation" },
  { icon: Layers, label: "131+ niche landing pages & 8 SEO templates for organic growth" },
];

export const WhatsNew = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        const timer = setTimeout(() => setOpen(true), 800);
        return () => clearTimeout(timer);
      }
    } catch (e) {
      /* localStorage blocked — silently skip */
    }
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, new Date().toISOString()); } catch (e) { /* ignore */ }
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: 32, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 24, scale: 0.96 }}
          transition={{ type: "spring", stiffness: 260, damping: 24 }}
          className="fixed bottom-6 right-6 z-[60] w-[min(92vw,360px)] rounded-2xl border border-violet-600/30 bg-[#0f0f17]/95 backdrop-blur-xl shadow-2xl shadow-violet-600/10"
          data-testid="whats-new-card"
        >
          <div className="p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-violet-600/15 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-violet-500" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-violet-500 font-semibold">What's New</p>
                  <p className="text-sm text-white font-medium">Fresh updates for you</p>
                </div>
              </div>
              <button
                onClick={dismiss}
                aria-label="Dismiss"
                className="text-white/40 hover:text-white transition-colors"
                data-testid="whats-new-close-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <ul className="space-y-2.5 mb-4">
              {HIGHLIGHTS.map((h, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 + i * 0.08 }}
                  className="flex items-start gap-2.5 text-sm text-white/80"
                >
                  <h.icon className="w-4 h-4 text-violet-500 mt-0.5 flex-shrink-0" />
                  <span>{h.label}</span>
                </motion.li>
              ))}
            </ul>

            <div className="flex items-center gap-2">
              <Link
                to="/dashboard/content"
                onClick={dismiss}
                className="flex-1 text-center text-sm font-medium px-3 py-2 rounded-full bg-violet-600 hover:bg-violet-700 text-white transition-colors"
                data-testid="whats-new-cta-btn"
              >
                Try it now
              </Link>
              <button
                onClick={dismiss}
                className="text-sm px-3 py-2 rounded-full text-white/60 hover:text-white transition-colors"
                data-testid="whats-new-later-btn"
              >
                Later
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
